import { useState, useEffect } from 'react';
import { supabase, isSupabaseAvailable } from '../lib/supabase';
import { useTenant } from './useTenant';

export interface CemeteryAIConfig {
  id: string;
  cemetery_id: string;
  ai_model: string;
  temperature: number;
  max_tokens: number;
  ai_personality: string;
  ai_tone: string;
  custom_instructions?: string;
  greeting_message: string;
  error_message: string;
  rag_enabled: boolean;
  rag_endpoint: string;
  rag_system_prompt?: string;
  rag_context_limit: number;
  rag_similarity_threshold: number;
  rag_temperature: number;
  rag_max_tokens: number;
  use_custom_rag_endpoint: boolean;
  created_at: string;
  updated_at: string;
}

interface UseCemeteryAIConfigResult {
  data: CemeteryAIConfig | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Cache for AI config data to avoid repeated fetches
const aiConfigCache = new Map<string, CemeteryAIConfig>();

/**
 * Custom hook to fetch cemetery AI configuration from Supabase
 */
export function useCemeteryAIConfig(): UseCemeteryAIConfigResult {
  const { tenant } = useTenant();
  const [data, setData] = useState<CemeteryAIConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAIConfig = async () => {
    try {
      console.log('🤖 useCemeteryAIConfig: Fetching AI config for tenant:', tenant?.id);
      setIsLoading(true);
      setError(null);
      
      if (!tenant?.id || !isSupabaseAvailable()) {
        console.log('⏳ Waiting for tenant ID or Supabase to be available');
        setIsLoading(false);
        return;
      }

      // Check cache first
      if (aiConfigCache.has(tenant.id)) {
        console.log('💾 Found cached AI config for:', tenant.id);
        const cachedConfig = aiConfigCache.get(tenant.id)!;
        setData(cachedConfig);
        setIsLoading(false);
        return;
      }

      // Fetch AI config from cemetery_ai_config table
      const { data: aiConfigData, error: fetchError } = await supabase!
        .from('cemetery_ai_config')
        .select('*')
        .eq('cemetery_id', tenant.id)
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          // No rows returned - this is okay, we'll use defaults
          console.log('ℹ️ No AI config found for cemetery, using defaults');
          setData(null);
        } else {
          console.error('Failed to fetch AI config:', fetchError);
          throw fetchError;
        }
      } else if (aiConfigData) {
        console.log('✅ Found cemetery AI config:', aiConfigData);
        // Cache the result
        aiConfigCache.set(tenant.id, aiConfigData);
        setData(aiConfigData);
      } else {
        console.log('ℹ️ No AI config data returned');
        setData(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch AI config';
      console.error('❌ useCemeteryAIConfig error:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const refetch = async () => {
    console.log('🔄 Refetching AI config for:', tenant?.id);
    // Clear cache for this tenant
    if (tenant?.id) {
      aiConfigCache.delete(tenant.id);
    }
    await fetchAIConfig();
  };

  useEffect(() => {
    if (tenant?.id) {
      console.log('🚀 Starting AI config fetch for:', tenant.id);
      fetchAIConfig();
    } else {
      console.log('⏸️ AI config fetch skipped - no tenant ID');
      setIsLoading(false);
    }
  }, [tenant?.id]);

  return {
    data,
    isLoading,
    error,
    refetch
  };
}